import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const PRINCIPLES = [{
  titleEn: 'Verify before answering.',
  titleFr: 'Vérifier avant de répondre.',
  descEn: 'Every answer should be grounded in verified information.',
  descFr: "Chaque réponse doit s'appuyer sur une information vérifiée."
}, {
  titleEn: 'Trust is infrastructure.',
  titleFr: 'La confiance est une infrastructure.',
  descEn: 'Communities adopt technology only when they trust it.',
  descFr: "Les collectivités n'adoptent une technologie que lorsqu'elles s'y fient."
}, {
  titleEn: 'AI must execute.',
  titleFr: "L'IA doit exécuter.",
  descEn: "Great AI doesn't stop at answers. It helps communities act.",
  descFr: "Une bonne IA ne s'arrête pas à la réponse. Elle aide les collectivités à agir."
}, {
  titleEn: 'One platform. Many communities.',
  titleFr: 'Une plateforme. Des collectivités multiples.',
  descEn: 'Every capability builds on the same trusted foundation.',
  descFr: 'Chaque capacité s’appuie sur la même fondation de confiance.'
}, {
  titleEn: 'Public infrastructure matters.',
  titleFr: "L'infrastructure publique compte.",
  descEn: 'We build technology that citizens actually use.',
  descFr: 'Nous bâtissons une technologie que les citoyens utilisent vraiment.'
}];
type TL = {
  year: {
    en: string;
    fr: string;
  };
  title: {
    en: string;
    fr: string;
  };
  detail?: {
    en: React.ReactNode;
    fr: React.ReactNode;
  };
  highlight?: boolean;
};
const TIMELINE: TL[] = [{
  year: {
    en: '2005',
    fr: '2005'
  },
  title: {
    en: 'Real-time public alerts',
    fr: 'Alertes publiques en temps réel'
  },
  detail: {
    en: 'Flight delays (Aéroports de Montréal, GTAA); transit schedules (STM, TransLink, TTC).',
    fr: 'Retards de vol (Aéroports de Montréal, GTAA); horaires de transport (STM, TransLink, TTC).'
  }
}, {
  year: {
    en: '2007',
    fr: '2007'
  },
  title: {
    en: 'World-first scannable mobile boarding pass',
    fr: 'Première mondiale : carte d’embarquement mobile scannable'
  },
  detail: {
    en: <>
          Commercialized with Air Canada and Aéroports de Montréal (LIPSO,
          2007) — the first 2D barcode scannable from a mobile screen. Patent{' '}
          <span className="font-mono text-[#4F46E5]" data-id="element-679">US 8,103,259</span> (V.
          Gravel et al., priority 2006).
        </>,
    fr: <>
          Commercialisée avec Air Canada et Aéroports de Montréal (LIPSO,
          2007) — le premier code-barres 2D scannable depuis l’écran d’un
          mobile. Brevet{' '}
          <span className="font-mono text-[#4F46E5]" data-id="element-680">US 8 103 259</span> (V.
          Gravel et al., priorité 2006).
        </>
  }
}, {
  year: {
    en: '2008',
    fr: '2008'
  },
  title: {
    en: 'Airport digital credentials',
    fr: "Titres d'accès numériques aéroportuaires"
  },
  detail: {
    en: "Nantes Airport, at Star Alliance's request.",
    fr: 'Aéroport de Nantes, à la demande de Star Alliance.'
  }
}, {
  year: {
    en: '2009',
    fr: '2009'
  },
  title: {
    en: 'Mobile parking',
    fr: 'Stationnement mobile'
  },
  detail: {
    en: 'Stationnement de Montréal — a North American pioneer.',
    fr: 'Stationnement de Montréal — pionnier en Amérique du Nord.'
  }
}, {
  year: {
    en: '2012',
    fr: '2012'
  },
  title: {
    en: 'Digital diagnostics — government & fintech AI',
    fr: 'Diagnostics numériques — gouvernement et IA fintech'
  },
  detail: {
    en: 'Québec government and fintech.',
    fr: 'Gouvernement du Québec et fintech.'
  }
}, {
  year: {
    en: '2014',
    fr: '2014'
  },
  title: {
    en: 'Digital health',
    fr: 'Santé numérique'
  }
}, {
  year: {
    en: '2017',
    fr: '2017'
  },
  title: {
    en: 'Verified municipal identity',
    fr: 'Identité municipale vérifiée'
  },
  detail: {
    en: 'City of Brossard — verified identity in production.',
    fr: "Ville de Brossard — l'identité vérifiée en production."
  }
}, {
  year: {
    en: 'Today',
    fr: "Aujourd'hui"
  },
  title: {
    en: 'AI-native municipal services',
    fr: 'Services municipaux nativement IA'
  },
  detail: {
    en: 'Waste, smart meters, requests — powered by ATNA.',
    fr: 'Matières résiduelles, compteurs intelligents, requêtes — propulsés par ATNA.'
  }
}, {
  year: {
    en: 'Tomorrow',
    fr: 'Demain'
  },
  title: {
    en: 'Trusted Intelligence Infrastructure',
    fr: "L'infrastructure d'intelligence de confiance"
  },
  highlight: true
}];
const PIONEER_ITEMS = [{
  en: 'World-first scannable mobile boarding pass',
  fr: 'Première mondiale : carte d’embarquement mobile scannable'
}, {
  en: 'Mobile parking pioneers',
  fr: 'Pionniers du stationnement mobile'
}, {
  en: 'Verified municipal identity',
  fr: 'Identité municipale vérifiée'
}, {
  en: 'AI-native municipal services',
  fr: 'Services municipaux nativement IA'
}];
const PROOF = [{
  stat: {
    en: 'Since 2017',
    fr: 'Depuis 2017'
  },
  label: {
    en: 'In continuous production',
    fr: 'En production continue'
  }
}, {
  stat: {
    en: '88,266',
    fr: '88\u00a0266'
  },
  label: {
    en: 'Verified identities · City of Brossard',
    fr: 'Identités vérifiées · Ville de Brossard'
  }
}, {
  stat: {
    en: '~78%',
    fr: '~78\u00a0%'
  },
  label: {
    en: 'Population coverage',
    fr: 'Couverture de la population'
  }
}];
// To activate a photo, paste its hosted URL into `img` (leave '' to show the placeholder).
const HOW_CARDS = [{
  num: '01',
  img: 'https://cdn.magicpatterns.com/uploads/d6EfCGkHEE4HHVpkok1pht/web_01_ecouter.jpg',
  titleEn: 'Listen',
  titleFr: 'Écouter',
  textEn: 'Every capability starts with real operational challenges. We work directly with municipalities, organizations and communities to understand how people actually work — not how software assumes they work.',
  textFr: 'Chaque capacité commence par de vrais défis opérationnels. Nous travaillons directement avec les municipalités, les organisations et les collectivités pour comprendre comment les gens travaillent vraiment — pas comme le logiciel le suppose.',
  tagEn: 'Built with communities.',
  tagFr: 'Bâti avec les collectivités.'
}, {
  num: '02',
  img: 'https://cdn.magicpatterns.com/uploads/tf9rBnqrxkRJS77gzAaUS7/web_02_concevoir.jpg',
  titleEn: 'Design',
  titleFr: 'Concevoir',
  textEn: "We design every interaction around simplicity, verified identity and trusted workflows. Our goal isn't to add features. It's to remove friction.",
  textFr: "Nous concevons chaque interaction autour de la simplicité, de l'identité vérifiée et de flux de confiance. Notre but n'est pas d'ajouter des fonctionnalités. C'est de retirer la friction.",
  tagEn: 'Designed for adoption.',
  tagFr: "Conçu pour l'adoption."
}, {
  num: '03',
  img: 'https://cdn.magicpatterns.com/uploads/cx3mS8ugfW5MvVszyxQzj6/web_03_batir_(1).jpg',
  titleEn: 'Build',
  titleFr: 'Bâtir',
  textEn: 'Every capability shares the same trusted foundation: verified identity, connected services, municipal data and the ATNA trusted intelligence engine. One platform. AI capabilities that expand module by module.',
  textFr: "Chaque capacité partage la même fondation de confiance : identité vérifiée, services connectés, données municipales et le moteur d'intelligence de confiance ATNA. Une plateforme. Des capacités d'IA qui s'étendent module par module.",
  tagEn: 'Built on one foundation.',
  tagFr: 'Bâti sur une seule fondation.'
}, {
  num: '04',
  img: 'https://cdn.magicpatterns.com/uploads/uDocxajproiww2teZ9DeSN/web_04_deployer.jpg',
  titleEn: 'Deploy',
  titleFr: 'Déployer',
  textEn: "Technology matters only when people use it. Our products are designed to operate in production, improve municipal capacity and earn citizens' trust every day.",
  textFr: "La technologie ne compte que lorsque les gens l'utilisent. Nos produits sont conçus pour fonctionner en production, accroître la capacité municipale et gagner la confiance des citoyens chaque jour.",
  tagEn: 'Built for real-world adoption.',
  tagFr: "Bâti pour l'adoption sur le terrain."
}];
// Paste Vivianne's hosted photo URL here to activate it (square crop recommended). Leave '' for the placeholder.
const FOUNDER_IMG = 'https://cdn.magicpatterns.com/uploads/gqXYvqWLfNkh7FAmd6Tmi2/ChatGPT_Image_7_juil._2026,_11_00_52.png';
export function About() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  useEffect(() => {
    track('Page Viewed', {
      page: 'About'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-681">
      <Header data-id="element-682" />
      <main className="pt-[120px]" data-id="element-683">
        {/* 1 — MISSION */}
        <section className="pt-28 pb-32 px-6" data-id="element-684">
          <div className="max-w-4xl mx-auto" data-id="element-685">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-10" data-id="element-686">
              {isFR ? 'ENTREPRISE' : 'COMPANY'}
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#121212] leading-[1.1] mb-12" data-id="element-687">
              {isFR ? "Nous bâtissons l'infrastructure d'intelligence de confiance pour les collectivités." : 'Building the Trusted Intelligence Infrastructure for Communities.'}
            </motion.h1>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="max-w-2xl space-y-4 font-sans text-xl text-[#121212]/70 leading-relaxed" data-id="element-688">
              <p data-id="element-689">
                {isFR ? "Pas une énième entreprise de logiciels municipaux. Pas une énième entreprise d'IA." : 'Not another municipal software company. Not another AI company.'}
              </p>
              <p className="text-[#121212]" data-id="element-690">
                {isFR ? 'Nous bâtissons l’infrastructure de confiance qui permet aux collectivités de vérifier, communiquer et agir avec assurance.' : 'We build the trusted infrastructure that lets communities verify, communicate and act with confidence.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* 2 — WHY WE EXIST */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-691">
          <div className="max-w-3xl mx-auto" data-id="element-692">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#5B6478] uppercase mb-10" data-id="element-693">
              {isFR ? 'Pourquoi nous existons' : 'Why we exist'}
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="space-y-2 mb-12" data-id="element-694">
              <p className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-695">
                {isFR ? "Les collectivités n'ont pas besoin de plus d'IA." : "Communities don't need more AI."}
              </p>
              <p className="font-serif text-3xl md:text-4xl text-[#4F46E5] leading-snug" data-id="element-696">
                {isFR ? 'Elles ont besoin d’une IA à laquelle se fier.' : 'They need AI they can trust.'}
              </p>
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} className="max-w-2xl space-y-6 font-sans text-lg text-[#121212]/70 leading-relaxed" data-id="element-697">
              <p data-id="element-698">
                {isFR ? 'La prochaine génération de services publics ne se bâtira pas sur une IA générique. Elle se bâtira sur l’identité vérifiée, l’information de confiance et l’exécution sécurisée.' : 'The next generation of public services won’t be built on generic AI. It will be built on verified identity, trusted information and secure execution.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]" data-id="element-699">
                {isFR ? "C'est pour cela que BCITI existe." : 'That is why BCITI exists.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* 3 — PRINCIPLES */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-700">
          <div className="max-w-4xl mx-auto" data-id="element-701">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#5B6478] uppercase mb-16" data-id="element-702">
              {isFR ? 'Nos principes' : 'What we believe'}
            </motion.div>
            <div className="divide-y divide-[#E4E6EC] border-y border-[#E4E6EC]" data-id="element-703">
              {PRINCIPLES.map((p, i) => <motion.div key={i} {...reveal} transition={{
              duration: 0.6,
              delay: i * 0.06
            }} className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-12 py-8" data-id="element-704">
                  <h3 className="font-serif text-2xl md:text-3xl text-[#121212] leading-tight" data-id="element-705">
                    {isFR ? p.titleFr : p.titleEn}
                  </h3>
                  <p className="font-sans text-lg text-[#5B6478] leading-relaxed md:pt-1" data-id="element-706">
                    {isFR ? p.descFr : p.descEn}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 4 — BUILT BY PIONEERS */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-707">
          <div className="max-w-4xl mx-auto" data-id="element-708">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#121212] leading-[1.15] mb-6" data-id="element-709">
              {isFR ? <>
                  Vingt ans à bâtir l'infrastructure publique numérique.
                  <br data-id="element-710" />
                  <span className="text-[#4F46E5]" data-id="element-711">
                    Aujourd'hui, l'IA municipale de confiance.
                  </span>
                </> : <>
                  Two decades building digital public infrastructure.
                  <br data-id="element-712" />
                  <span className="text-[#4F46E5]" data-id="element-713">
                    Now building trusted municipal AI.
                  </span>
                </>}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-lg text-[#5B6478] leading-relaxed max-w-2xl mb-12" data-id="element-714">
              {isFR ? 'BCITI est bâtie par les personnes derrière certaines des premières du numérique public en Amérique du Nord — souvent premières, souvent copiées ensuite.' : 'BCITI is built by the people behind some of North America’s public-sector firsts — often first, often copied.'}
            </motion.p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-715">
              {PIONEER_ITEMS.map((item, i) => <motion.div key={i} {...reveal} transition={{
              duration: 0.5,
              delay: i * 0.08
            }} className="flex items-start gap-3 border border-[#E4E6EC] bg-white p-5" data-id="element-716">
                  <CheckIcon className="w-5 h-5 text-[#4F46E5] shrink-0 mt-0.5" strokeWidth={2} data-id="element-717" />
                  <span className="font-sans text-base text-[#121212] leading-snug" data-id="element-718">
                    {isFR ? item.fr : item.en}
                  </span>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 5 — JOURNEY (timeline) */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-719">
          <div className="max-w-3xl mx-auto" data-id="element-720">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#5B6478] uppercase mb-16" data-id="element-721">
              {isFR ? 'Notre parcours' : 'Our journey'}
            </motion.div>
            <div className="relative border-l border-[#E4E6EC] ml-2" data-id="element-722">
              {TIMELINE.map((t, i) => <motion.div key={i} {...reveal} transition={{
              duration: 0.5,
              delay: Math.min(i * 0.05, 0.3)
            }} className="relative pl-8 pb-12 last:pb-0" data-id="element-723">
                  <span className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 ${t.highlight ? 'bg-[#4F46E5]' : 'bg-[#121212]'}`} data-id="element-724" />
                  <div className={`font-sans text-sm font-semibold tracking-wide uppercase mb-1 ${t.highlight ? 'text-[#4F46E5]' : 'text-[#5B6478]'}`} data-id="element-725">
                    {isFR ? t.year.fr : t.year.en}
                  </div>
                  <div className={`font-serif text-xl md:text-2xl leading-snug ${t.highlight ? 'text-[#4F46E5]' : 'text-[#121212]'}`} data-id="element-726">
                    {isFR ? t.title.fr : t.title.en}
                  </div>
                  {t.detail && <p className="font-sans text-base text-[#5B6478] leading-relaxed mt-1.5" data-id="element-727">
                      {isFR ? t.detail.fr : t.detail.en}
                    </p>}
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 6 — PROOF */}
        <section className="py-24 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-728">
          <div className="max-w-5xl mx-auto" data-id="element-729">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-730">
              {PROOF.map((card, i) => <motion.div key={i} {...reveal} transition={{
              duration: 0.6,
              delay: i * 0.1
            }} className="bg-white p-10" data-id="element-731">
                  <div className="font-serif text-4xl md:text-5xl text-[#121212] mb-4" data-id="element-732">
                    {isFR ? card.stat.fr : card.stat.en}
                  </div>
                  <div className="font-sans text-sm text-[#121212]/60 leading-relaxed" data-id="element-733">
                    {isFR ? card.label.fr : card.label.en}
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* 7 — HOW WE BUILD */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-734">
          <div className="max-w-5xl mx-auto" data-id="element-735">
            <motion.div {...reveal} className="max-w-3xl mb-16" data-id="element-736">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-8" data-id="element-737">
                {isFR ? 'COMMENT NOUS BÂTISSONS' : 'HOW WE BUILD'}
              </div>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#121212] leading-[1.15] mb-6" data-id="element-738">
                {isFR ? "Des idées à l'infrastructure publique de confiance." : 'From ideas to trusted public infrastructure.'}
              </h2>
              <p className="font-sans text-lg text-[#5B6478] leading-relaxed" data-id="element-739">
                {isFR ? "La technologie ne crée de la valeur que lorsqu'on s'y fie et que les collectivités l'adoptent. Chaque capacité BCITI suit le même processus — de la compréhension des défis opérationnels à la livraison d'une IA de confiance en production." : 'Technology only creates value when people trust it and communities adopt it. Every BCITI capability follows the same process — from understanding operational challenges to delivering trusted AI in production.'}
              </p>
            </motion.div>

            {/* Signature journey strip */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="border-y border-[#E4E6EC] py-6 mb-16" data-id="element-740">
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2" data-id="element-741">
                {[{
                en: 'Listen',
                fr: 'Écouter'
              }, {
                en: 'Design',
                fr: 'Concevoir'
              }, {
                en: 'Build',
                fr: 'Bâtir'
              }, {
                en: 'Deploy',
                fr: 'Déployer'
              }, {
                en: 'Learn',
                fr: 'Apprendre'
              }].map((s, i, arr) => <Fragment key={i} data-id="element-742">
                    <span className={`font-sans text-sm font-semibold tracking-wide uppercase ${i === arr.length - 1 ? 'text-[#4F46E5]' : 'text-[#121212]'}`} data-id="element-743">
                      {isFR ? s.fr : s.en}
                    </span>
                    {i < arr.length - 1 && <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]/50 shrink-0" data-id="element-744" />}
                  </Fragment>)}
              </div>
              <p className="text-center font-sans text-sm text-[#5B6478] mt-4" data-id="element-745">
                {isFR ? 'Chaque capacité BCITI suit le même parcours.' : 'Every BCITI capability follows the same journey.'}
              </p>
            </motion.div>

            {/* Four editorial cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-746">
              {HOW_CARDS.map((c, i) => <motion.div key={i} {...reveal} transition={{
              duration: 0.6,
              delay: i * 0.08
            }} className="bg-white flex flex-col" data-id="element-747">
                  {c.img ? <img src={c.img} alt={isFR ? c.titleFr : c.titleEn} className="w-full aspect-[16/10] object-cover border-b border-[#E4E6EC]" data-id="element-748" /> : <div className="w-full aspect-[16/10] bg-[#FAFAFB] border-b border-[#E4E6EC] flex items-center justify-center" data-id="element-749">
                      <span className="font-sans text-xs tracking-wide uppercase text-[#5B6478]" data-id="element-750">
                        Photo · {isFR ? c.titleFr : c.titleEn}
                      </span>
                    </div>}
                  <div className="p-8 md:p-10 flex flex-col flex-1" data-id="element-751">
                    <div className="font-mono text-sm text-[#4F46E5] mb-4" data-id="element-752">
                      {c.num}
                    </div>
                    <h3 className="font-serif text-2xl text-[#121212] mb-4" data-id="element-753">
                      {isFR ? c.titleFr : c.titleEn}
                    </h3>
                    <p className="font-sans text-base text-[#5B6478] leading-relaxed flex-1" data-id="element-754">
                      {isFR ? c.textFr : c.textEn}
                    </p>
                    <div className="mt-6 pt-4 border-t border-[#E4E6EC] font-sans text-xs font-semibold tracking-wide uppercase text-[#4F46E5]" data-id="element-755">
                      {isFR ? c.tagFr : c.tagEn}
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* Final statement */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="text-center max-w-3xl mx-auto mt-24" data-id="element-756">
              <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#121212] leading-[1.15]" data-id="element-757">
                {isFR ? 'La technologie ne bâtit pas la confiance.' : "Technology doesn't build trust."}
              </p>
              <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#4F46E5] leading-[1.15] mb-8" data-id="element-758">
                {isFR ? 'Les gens, oui.' : 'People do.'}
              </p>
              <p className="font-sans text-lg text-[#5B6478] leading-relaxed" data-id="element-759">
                {isFR ? "Depuis près de deux décennies, notre équipe transforme les technologies émergentes en infrastructure publique de confiance. Aujourd'hui, nous le faisons de nouveau avec l'IA." : "For nearly two decades, our team has transformed emerging technologies into trusted public infrastructure. Today, we're doing it again with AI."}
              </p>
            </motion.div>
          </div>
        </section>

        {/* 8 — FOUNDER */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-760">
          <div className="max-w-4xl mx-auto" data-id="element-761">
            <motion.div {...reveal} className="font-sans text-xs font-semibold tracking-[0.2em] text-[#5B6478] uppercase mb-12" data-id="element-762">
              {isFR ? 'Fondatrice' : 'Founder'}
            </motion.div>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="border border-[#E4E6EC] max-w-3xl flex flex-col" data-id="element-763">
              {FOUNDER_IMG ? <img src={FOUNDER_IMG} alt="Vivianne Gravel" className="w-full aspect-[3/2] object-cover border-b border-[#E4E6EC]" data-id="element-764" /> : <div className="w-full aspect-[3/2] bg-[#FAFAFB] border-b border-[#E4E6EC] flex items-center justify-center" data-id="element-765">
                  <span className="font-sans text-xs tracking-wide uppercase text-[#5B6478]" data-id="element-766">
                    Photo · Vivianne Gravel
                  </span>
                </div>}
              <div className="p-8 md:p-10" data-id="element-767">
                <div className="font-serif text-2xl text-[#121212] mb-1" data-id="element-768">
                  Vivianne Gravel
                </div>
                <div className="font-sans text-sm font-semibold tracking-wide uppercase text-[#4F46E5] mb-5" data-id="element-769">
                  {isFR ? 'Fondatrice et PDG' : 'Founder & CEO'}
                </div>
                <p className="font-sans text-base text-[#5B6478] leading-relaxed" data-id="element-770">
                  {isFR ? "Depuis plus de deux décennies, Vivianne bâtit des innovations numériques qui ont transformé la façon dont les gens interagissent avec les services publics — des télécommunications et des médias à l'aviation, au transport, à la santé, au gouvernement et aux municipalités. Elle dirige aujourd'hui la mission de BCITI\u00a0: bâtir l'infrastructure d'intelligence de confiance pour les collectivités." : "For over two decades, Vivianne has built digital innovations that transformed how people interact with public services — from telecommunications and media to aviation, transportation, healthcare, government and municipalities. Today, she leads BCITI's mission to build the Trusted Intelligence Infrastructure for Communities."}
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 9 — VISION + JOIN US */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-771">
          <div className="max-w-3xl mx-auto text-center" data-id="element-772">
            <motion.p {...reveal} className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] text-[#121212] leading-[1.2] mb-8" data-id="element-773">
              {isFR ? "Nous croyons que l'intelligence de confiance deviendra aussi essentielle aux collectivités que les routes, l'eau et les communications." : 'We believe trusted intelligence will become as essential to communities as roads, water and communications.'}
            </motion.p>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-serif text-2xl md:text-3xl text-[#4F46E5] mb-14" data-id="element-774">
              {isFR ? 'BCITI bâtit cette infrastructure.' : 'BCITI is building that infrastructure.'}
            </motion.p>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-col items-center gap-6" data-id="element-775">
              <div className="font-sans text-lg text-[#5B6478]" data-id="element-776">
                BCITI Inc. · Québec, Canada ·{' '}
                <a href="mailto:contact@bciti.com" className="text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-777">
                  contact@bciti.com
                </a>
              </div>
              <Link to="/request-demo" className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-778">
                {isFR ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-779" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer data-id="element-780" />
    </div>;
}